import { useState, type SyntheticEvent, useMemo, type HTMLAttributes, useCallback } from 'react'
import {
  Autocomplete as MuiAutocomplete,
  type AutocompleteProps as MuiAutocompleteProps,
  type AutocompleteCloseReason,
  type AutocompleteRenderOptionState,
  type AutocompleteFreeSoloValueMapping,
} from '@mui/material'
import type { SystemCssProperties } from '@dhl-ui/theme'
import {
  getOptionLabel as defaultGetOptionLabel,
  getOptionValue,
  isOptionEqualToValue as isDefaultOptionEqualToValue,
  isSelectAllOption,
  type SelectAllOption,
  SelectAllOptionValue,
} from './helpers'
import {
  autocompleteOptionRenderer,
  renderInput,
  renderMultipleValue,
  type RenderInputProps,
  Popper,
} from './components'
import { type AutocompleteOption } from './model'
import { type DataTestIdAttribute } from '../../types'

export type AutocompleteProps<
  Value = AutocompleteOption<string>,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
> = Omit<
  MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>,
  'sx' | 'renderInput' | 'slotProps' | 'slots' | 'getLimitTagsText'
> &
  DataTestIdAttribute & {
    sx?: Pick<SystemCssProperties, 'flex'>
    /**
     * when true, shows an additional Checkbox option to select/unselect all options. Only applicable when `multiple` is true.
     */
    withSelectAll?: boolean
    /**
     * Label for the "Select all" option. Only applicable when `withSelectAll` is true.
     */
    selectAllLabel?: string
    /**
     * Label for the "Unselect all" option. Only applicable when `withSelectAll` is true.
     */
    unselectAllLabel?: string
  } & Pick<
    RenderInputProps<Value, Multiple, DisableClearable, FreeSolo>,
    'label' | 'placeholder' | 'required' | 'helperText' | 'error' | 'name' | 'slotProps'
  >

/**
 * Autocomplete
 *
 * Demos:
 * - [Autocomplete](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/inputs-autocomplete--docs)
 *
 */

export const Autocomplete = <
  Value = AutocompleteOption<string>,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
>({
  options = [],
  'data-testid': dataTestId,
  placeholder,
  getOptionLabel = defaultGetOptionLabel<Value>,
  isOptionEqualToValue = isDefaultOptionEqualToValue<Value>,
  size = 'medium',
  loading,
  label,
  name,
  required,
  helperText,
  error,
  slotProps,
  renderValue,
  // (un) select all props:
  withSelectAll,
  selectAllLabel = 'Select all',
  unselectAllLabel = 'Unselect all',
  value,
  // onChange
  onChange,
  // rest
  ...props
}: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>) => {
  /**
   * open|close state:
   */
  const [open, setOpen] = useState<boolean>(false)
  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = (event: SyntheticEvent, reason: AutocompleteCloseReason) => {
    // make sure NOT to close this when using in multiple
    if (reason === 'removeOption' || (!!props.multiple && reason === 'selectOption')) {
      event.preventDefault()
    } else {
      setOpen(false)
    }
  }

  /**
   * SELECT ALL - start
   */
  const isMultiple = Boolean(props.multiple)

  const withSelectAllEnabled = isMultiple && withSelectAll === true

  const selectAllOption = useMemo<SelectAllOption>(
    () => ({
      value: SelectAllOptionValue,
      label: selectAllLabel,
    }),
    [selectAllLabel]
  )

  const selectedValues = useMemo<Value[]>(
    // reason: still readable
    // eslint-disable-next-line no-nested-ternary
    () => (isMultiple ? (Array.isArray(value) ? value : []) : []),
    [isMultiple, value]
  )
  const selectedOptionsCount = useMemo(() => {
    const selectedValuesSet = new Set(selectedValues.map((selectedValue) => getOptionValue<Value>(selectedValue)))

    return options.reduce((count, option) => count + Number(selectedValuesSet.has(getOptionValue<Value>(option))), 0)
  }, [options, selectedValues])

  const allSelected = options.length > 0 && selectedOptionsCount === options.length
  const partiallySelected = selectedOptionsCount > 0 && !allSelected
  const toggleAllLabel = allSelected ? unselectAllLabel : selectAllLabel
  /**
   * SELECT ALL - END
   */

  /**
   * we invoke the onChange to deal with the master checkbox (if applicable)
   */
  const handleChange: MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>['onChange'] = (
    event,
    newValue,
    reason,
    details
  ) => {
    if (
      withSelectAllEnabled &&
      Array.isArray(newValue) &&
      newValue.some((selectedOption) => isSelectAllOption(selectedOption))
    ) {
      const nextSelection = allSelected ? [] : options
      onChange?.(
        event,
        nextSelection as NonNullable<MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>['value']>,
        reason
      )
    } else {
      onChange?.(event, newValue, reason, details)
    }
  }

  const renderInputProps: RenderInputProps<Value, Multiple, DisableClearable, FreeSolo> = {
    size,
    loading,
    required,
    placeholder,
    'data-testid': dataTestId,
    label,
    name,
    helperText,
    error,
    slotProps,
  }

  const getOptionLabelWithSelectAll = useCallback(
    (option: Value | AutocompleteFreeSoloValueMapping<FreeSolo>): string =>
      isSelectAllOption(option) ? toggleAllLabel : getOptionLabel(option),
    [toggleAllLabel, getOptionLabel]
  )

  const isOptionEqualToValueWithSelectAll = (option: Value, optionValue: Value): boolean =>
    isSelectAllOption(option) && isSelectAllOption(optionValue) ? true : isOptionEqualToValue(option, optionValue)

  const defaultRenderOption = autocompleteOptionRenderer<Value, FreeSolo>(getOptionLabelWithSelectAll, isMultiple)

  const renderOption = (
    liProps: HTMLAttributes<HTMLLIElement>,
    option: Value,
    state: AutocompleteRenderOptionState
  ) => {
    if (!withSelectAllEnabled || !isSelectAllOption<Value>(option)) {
      return defaultRenderOption(liProps, option, state)
    }
    return defaultRenderOption(liProps, option, {
      ...state,
      selected: allSelected,
      indeterminate: partiallySelected,
      withDivider: true,
    })
  }

  const renderedOptions = useMemo<Value[]>(
    () => (withSelectAllEnabled ? ([selectAllOption, ...options] as Value[]) : [...options]),
    [withSelectAllEnabled, selectAllOption, options]
  )

  // reason: still readable
  // eslint-disable-next-line no-nested-ternary
  const valueProps = isMultiple
    ? {
        value: selectedValues as NonNullable<
          MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>['value']
        >,
      }
    : value === undefined
      ? {}
      : { value }

  return (
    <MuiAutocomplete<Value, Multiple, DisableClearable, FreeSolo>
      renderOption={renderOption}
      getOptionLabel={getOptionLabelWithSelectAll}
      {...props} // move accordingly
      {...valueProps}
      onChange={handleChange}
      open={open}
      loading={loading}
      onOpen={handleOpen}
      onClose={handleClose}
      data-testid={dataTestId}
      renderInput={renderInput<Value, Multiple, DisableClearable, FreeSolo>(renderInputProps)}
      renderValue={isMultiple ? renderMultipleValue<Value, Multiple, DisableClearable, FreeSolo> : undefined}
      options={renderedOptions}
      isOptionEqualToValue={isOptionEqualToValueWithSelectAll}
      slotProps={{
        chip: {
          size: 'small', // always use the small
        },
        clearIndicator: {
          size: 'xs', // always use the xs
        },
        popupIndicator: {
          size: 'xs', // always use the xs
        },
        popper: {
          component: Popper,
        },
      }}
    />
  )
}
